import { useState, type ChangeEvent, type MouseEvent } from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@predoc/cow';
import { Check, ChevronDown, Hospital } from 'lucide-react';
import { useDebounce } from '@/hooks';
import {
  useGetOrgProfileById,
  useListProfiles,
} from '@/feature/profiles/hooks/profilesAPI';
import { getProfileColor } from '@/utils';
import { cn } from '@/lib/utils';

interface EfaxProfileSelectorProps {
  value: string;
  onChange: (profileId: string) => void;
}

export const EfaxProfileSelector = ({
  value,
  onChange,
}: EfaxProfileSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: profilesData, isLoading } = useListProfiles({
    searchTerms: debouncedSearch ? [debouncedSearch] : undefined,
    pagination: { offset: 0, limit: 100 },
  });

  const profiles = profilesData?.data ?? [];

  const selectedFromList = profiles.find((profile) => profile.id === value);
  const { data: selectedProfileData } = useGetOrgProfileById(
    value && !selectedFromList ? value : undefined,
  );
  const selectedProfile = selectedFromList ?? selectedProfileData ?? null;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearchTerm('');
    }
    setOpen(next);
  };

  const handleSelect = (profile: {
    id: string;
    displayName: string;
    avatarType: string;
  }) => {
    setOpen(false);
    setSearchTerm('');
    onChange(profile.id);
  };

  const triggerColor = selectedProfile
    ? getProfileColor(selectedProfile.avatarType)
    : undefined;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'border-input bg-background h-9 w-full justify-between rounded-md border px-3 font-normal',
            selectedProfile && 'border-blue-400 bg-blue-50',
          )}
          aria-label="Select profile"
        >
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {selectedProfile ? (
              <>
                <Hospital
                  className="size-4 shrink-0"
                  style={{ color: triggerColor }}
                />
                <span className="truncate text-sm">
                  {selectedProfile.displayName}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground truncate text-sm">
                Any profile
              </span>
            )}
          </div>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="p-2">
          <Input
            placeholder="Search profiles..."
            value={searchTerm}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(event.target.value)
            }
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <span className="text-muted-foreground text-sm">Loading...</span>
            </div>
          ) : profiles.length === 0 ? (
            <div className="flex items-center justify-center p-4">
              <span className="text-muted-foreground text-sm">
                No profiles found.
              </span>
            </div>
          ) : (
            <div className="p-1">
              {profiles.map((profile) => {
                const color = getProfileColor(profile.avatarType);
                const isSelected = profile.id === value;

                return (
                  <Button
                    key={profile.id}
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start gap-2 px-2 py-1.5"
                    onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      handleSelect(profile);
                    }}
                  >
                    <Hospital className="size-4 shrink-0" style={{ color }} />
                    <span className="flex-1 text-left text-sm">
                      {profile.displayName}
                    </span>
                    {isSelected ? <Check className="size-4 shrink-0" /> : null}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
